import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { useSession } from '@/session/store';
import {
  searchVoices,
  voiceChoice,
  voiceChoices,
  voiceChoiceValue,
  voiceGroupLabel,
  type VoiceChoice,
} from '@/session/tts';

const LISTBOX_ID = 'voice-picker-listbox';
const optionId = (index: number) => `voice-picker-option-${index}`;

/**
 * Picks the TTS provider, model and voice a session signs in with, as one value — the same control
 * and the same terms as the model picker. Every row is a complete selection, grouped under its
 * provider and model, so "rachel turbo" or "polly male" finds it without stepping through three
 * dependent selects. The first row names nothing and leaves the backend on its own default.
 *
 * The list comes from the backend's `/tts/catalog`; until it loads (or when it fails) the trigger
 * says so in place, keeping the layout the same.
 */
export function VoicePicker({ className, id }: { className?: string; id?: string }) {
  const provider = useSession((state) => state.ttsProvider);
  const model = useSession((state) => state.ttsModel);
  const voice = useSession((state) => state.ttsVoice);
  const setTts = useSession((state) => state.setTts);
  const catalog = useSession((state) => state.ttsCatalog);
  const status = useSession((state) => state.ttsCatalogStatus);
  const catalogError = useSession((state) => state.ttsCatalogError);
  const loadTtsCatalog = useSession((state) => state.loadTtsCatalog);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const choices = useMemo(() => voiceChoices(catalog), [catalog]);
  const selected = useMemo(
    () => voiceChoice(catalog, { provider, model, voice }),
    [catalog, provider, model, voice],
  );
  const matches = useMemo(() => searchVoices(query, choices), [query, choices]);
  // Clamped rather than stored back: the list shrinks as the query grows.
  const active = matches.length === 0 ? -1 : Math.min(activeIndex, matches.length - 1);

  function choose(choice: VoiceChoice): void {
    setTts(voiceChoiceValue(choice));
    setOpen(false);
  }

  if (status === 'idle' || status === 'loading') {
    return (
      <button
        type="button"
        id={id}
        disabled
        className={cn('flex w-full items-center gap-2 text-left', className)}
      >
        <span className="text-faint min-w-0 flex-1 truncate">Loading voices…</span>
        <ChevronsUpDown className="text-faint size-3.5 shrink-0" />
      </button>
    );
  }

  if (status === 'error') {
    return (
      <button
        type="button"
        id={id}
        onClick={loadTtsCatalog}
        title={catalogError ?? undefined}
        className={cn('flex w-full items-center gap-2 text-left', className)}
      >
        <span className="text-muted min-w-0 flex-1 truncate">Could not load voices</span>
        <span className="text-accent-soft shrink-0 text-[10px] tracking-widest uppercase">
          Retry
        </span>
      </button>
    );
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Every opening starts from the whole list, with the current voice highlighted.
        if (next) {
          setQuery('');
          setActiveIndex(
            Math.max(
              0,
              choices.findIndex((choice) => choice.id === selected.id),
            ),
          );
        }
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          className={cn('flex w-full items-center gap-2 text-left', className)}
        >
          <span className="min-w-0 flex-1 truncate">{selected.label}</span>
          {selected.kind === 'voice' && (
            <span className="text-faint shrink-0 truncate text-[10px] tracking-widest uppercase">
              {voiceGroupLabel(selected)}
            </span>
          )}
          <ChevronsUpDown className="text-faint size-3.5 shrink-0" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="border-line bg-surface z-50 w-[max(var(--radix-popover-trigger-width),22rem)] overflow-hidden rounded-xl border font-sans shadow-2xl"
        >
          <div className="border-line-soft flex items-center gap-2 border-b px-3 py-2">
            <Search className="text-faint size-3.5 shrink-0" />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (matches.length === 0) return;
                  const step = event.key === 'ArrowDown' ? 1 : -1;
                  setActiveIndex((current) => {
                    const from = Math.min(current, matches.length - 1);
                    return (from + step + matches.length) % matches.length;
                  });
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  const choice = matches[active];
                  if (choice) choose(choice);
                }
              }}
              role="combobox"
              aria-expanded
              aria-controls={LISTBOX_ID}
              aria-autocomplete="list"
              aria-activedescendant={active < 0 ? undefined : optionId(active)}
              aria-label="Filter voices"
              placeholder="Filter by provider, model or voice"
              spellCheck={false}
              autoComplete="off"
              className="text-text placeholder:text-faint min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </div>

          <ul id={LISTBOX_ID} role="listbox" className="scroll-slim max-h-72 overflow-y-auto py-1">
            {matches.map((choice, index) => (
              <Option
                key={choice.id}
                choice={choice}
                index={index}
                active={index === active}
                selected={choice.id === selected.id}
                heading={
                  index === 0 || voiceGroupLabel(matches[index - 1]) !== voiceGroupLabel(choice)
                }
                onHover={() => setActiveIndex(index)}
                onSelect={() => choose(choice)}
              />
            ))}

            {matches.length === 0 && (
              <li className="text-muted px-3 py-6 text-center text-xs">
                No voice matches “{query.trim()}”.
              </li>
            )}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Option({
  choice,
  index,
  active,
  selected,
  heading,
  onHover,
  onSelect,
}: {
  choice: VoiceChoice;
  index: number;
  active: boolean;
  selected: boolean;
  heading: boolean;
  onHover: () => void;
  onSelect: () => void;
}) {
  const row = useRef<HTMLLIElement>(null);

  // Keyboard navigation has to bring its target into view.
  useEffect(() => {
    if (active) row.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return (
    <>
      {heading && (
        <li
          role="presentation"
          className="text-faint flex items-center gap-2 px-3 pt-2 pb-1 text-[10px] font-semibold tracking-widest uppercase"
        >
          <span className="min-w-0 flex-1 truncate">{voiceGroupLabel(choice)}</span>
          {choice.kind === 'voice' && !choice.available && (
            <span className="shrink-0 font-normal">unavailable</span>
          )}
        </li>
      )}
      {/* The ARIA combobox pattern keeps the keyboard on the input — the options are never focused,
          and their id is what `aria-activedescendant` points at — so the click handler here has no
          keyboard listener beside it by design. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <li
        ref={row}
        id={optionId(index)}
        role="option"
        aria-selected={selected}
        onMouseEnter={onHover}
        onClick={onSelect}
        className={cn(
          'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
          active && 'bg-hover',
        )}
      >
        <Check
          className={cn('text-accent-soft size-3.5 shrink-0', !selected && 'invisible')}
          aria-hidden
        />
        <span className={cn('min-w-0 flex-1 truncate', selected ? 'text-text' : 'text-muted')}>
          {choice.label}
        </span>
        <span className="text-faint shrink-0 truncate text-[10px]">
          {choice.kind === 'voice' ? choice.gender : 'backend picks the voice'}
        </span>
      </li>
    </>
  );
}
